import { useEffect, useState } from 'react'

import { Asset } from 'types'

import ListView from 'components/partials/ListView'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSPagination from 'designSystem/components/PDSPagination/PDSPagination'
import PDSSearchBar from 'designSystem/components/PDSSearchBar/PDSSearchBar'
import PDSText from 'designSystem/components/PDSText/PDSText'
import { COLOR } from 'designSystem/foundations/constants/color'
import { FONT_SIZE } from 'designSystem/foundations/constants/fontSize'
import { SIZE } from 'designSystem/foundations/constants/size'
import { jsonClone } from 'utils/generalUtils'
import { searchFilter } from 'utils/searchFilter'

import AssetRowV2 from '../../AssetRowV2'

type Props = {
  assets: Asset[]
  setAssets: (assets: Asset[]) => void
  readOnly?: boolean
  showTradableSwitches?: boolean
}

const PAGE_SIZE = 12

function InvestmentPreferenceAssetTable({
  assets,
  setAssets,
  readOnly = false,
  showTradableSwitches = false,
}: Props) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [assetList, setAssetList] = useState(assets)
  const [assetsCount, setAssetsCount] = useState(assets.length)

  const handleChange = (target: HTMLInputElement, id: string) => {
    const { name, value } = target
    const updatedAssets = [...assets]

    const index = assets.findIndex((asset) => asset.id === id)

    updatedAssets[index] = Object.assign({}, updatedAssets[index], {
      [name]: name === 'symbol' ? value : parseFloat(value),
    })

    setAssets(updatedAssets)
  }

  const handleDelete = (deletingAsset: Asset) => {
    setAssets([
      ...assets.filter(({ symbol }) => symbol !== deletingAsset.symbol),
    ])
  }

  useEffect(() => {
    let clonedAssets = jsonClone(assets) as Asset[]

    const filteredAssets = clonedAssets.filter(
      (item) =>
        searchFilter(item, 'name', search) ||
        searchFilter(item, 'symbol', search)
    )

    const assetsToShow = filteredAssets.slice(
      (page - 1) * PAGE_SIZE,
      page * PAGE_SIZE
    )

    setAssetList(assetsToShow)
    setAssetsCount(filteredAssets.length)
  }, [assets, page, search, setAssetsCount, setAssetList])

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    setPage(1)
  }

  return (
    <PDSBox>
      <PDSBox marginBottom={SIZE.s8}>
        <PDSText
          color={COLOR.grey600}
          fontSize={FONT_SIZE.fs14}
          fontWeight={500}
        >
          Asset List
        </PDSText>
      </PDSBox>

      <PDSBox marginBottom={SIZE.s24}>
        <PDSSearchBar
          placeholder="Search here..."
          value={search}
          onChange={handleSearch}
        />
      </PDSBox>

      <ListView
        data={assetList}
        gap={SIZE.s12}
        header={
          <PDSFlexBox marginX={SIZE.s8}>
            <PDSFlexBox flex={1} justifyContent="left">
              <PDSText
                color={COLOR.grey600}
                fontSize={FONT_SIZE.fs14}
                fontWeight={500}
              >
                Name
              </PDSText>
            </PDSFlexBox>
            <PDSFlexBox flex={1} justifyContent="center">
              <PDSText
                color={COLOR.grey600}
                fontSize={FONT_SIZE.fs14}
                fontWeight={500}
              >
                Price
              </PDSText>
            </PDSFlexBox>
            <PDSFlexBox flex={1} justifyContent="center">
              <PDSText
                color={COLOR.grey600}
                fontSize={FONT_SIZE.fs14}
                fontWeight={500}
              >
                Quantity
              </PDSText>
            </PDSFlexBox>
            <PDSFlexBox flex={1} justifyContent="center">
              <PDSText
                color={COLOR.grey600}
                fontSize={FONT_SIZE.fs14}
                fontWeight={500}
              >
                Total Value
              </PDSText>
            </PDSFlexBox>
            <PDSFlexBox flex={1} justifyContent="center">
              <PDSText
                color={COLOR.grey600}
                fontSize={FONT_SIZE.fs14}
                fontWeight={500}
              >
                % Portfolio
              </PDSText>
            </PDSFlexBox>
            {showTradableSwitches || (
              <>
                <PDSFlexBox flex={1} justifyContent="flex-end">
                  <PDSText
                    color={COLOR.grey600}
                    fontSize={FONT_SIZE.fs14}
                    fontWeight={500}
                  >
                    Tradable?
                  </PDSText>
                </PDSFlexBox>
                <PDSFlexBox />
              </>
            )}
          </PDSFlexBox>
        }
        renderItem={(asset) => {
          return (
            <AssetRowV2
              key={asset.id}
              asset={asset}
              isEditingAsset={true}
              handleChange={(target: any) => handleChange(target, asset.id)}
              handleDelete={handleDelete}
              readOnly={readOnly}
              showTradableSwitches={showTradableSwitches}
            />
          )
        }}
      />
      <PDSFlexBox justifyContent="center" marginTop={SIZE.s16}>
        <PDSPagination
          currentPage={page}
          onPageChange={handlePageChange}
          pageSize={PAGE_SIZE}
          totalCount={assetsCount}
        />
      </PDSFlexBox>
    </PDSBox>
  )
}

export default InvestmentPreferenceAssetTable
